<script>
	import thumb_u2ILNHzT8FM from '$lib/Components/Youtube/images/u2ILNHzT8FM.jpg?w=610';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import filingNoiseComplaintsCityBinder from './images/filing-noise-complaints-city-binder.jpg';
	import IwNoiseLevelInfographic from './images/IW-Noise-Level-Infographic.jpg';
	import noiseComplaintsLoudNeighborsParty from './images/noise-complaints-loud-neighbors-party.jpg';
	import resourcesSoundproofing from './images/resources-soundproofing.png';
	import solutionsComfortSavingsPerformance from './images/solutions-comfort-savings-performance-bottom.png';
</script>

<Section>
	<p>
		Sometimes the noise around us gets so bad it doesn't seem like there's
		anything we can do other than complain. We've talked to the neighbors, the
		landlord. We've added noise machines, moved the bed…nothing has worked! So,
		we will tell you how file a noise complaint online.
	</p>
	<Image src={noiseComplaintsLoudNeighborsParty} alt="loud neighbors party" />
	<p>
		Bringing in outside enforcement, like the police, for noise complaints can
		escalate a bad situation. If you file a noise complaint online, will it
		help? Can you make an anonymous noise complaint?
	</p>
	<p class="italic">
		Note: If any of the noises you are hearing could be related to domestic
		abuse or violence, call 911. They can keep you anonymous and help resolve
		the situation.
	</p>
	<Bubble>
		<p class="mb-6 text-xl font-bold">File a Noise Complaint & See Results</p>
		<ul>
			<li>
				<a href="#charge">Contact the right person</a>
			</li>
			<li>
				<a href="#ordinance">Know the law & use it to your advantage</a>
			</li>
			<li>
				<a href="#file">File a noise complaint anonymously & correctly</a>
			</li>
			<li>
				<a href="#noise">What to do if the noise continues</a>
			</li>
		</ul>
	</Bubble>
</Section>

<Section id="charge">
	<h2>Who Is in Charge of Noise Complaints</h2>
	<p>
		There's most likely a local noise complaint number to call. If there isn't
		(or you can't find it) call the non-emergency line. Police appreciate
		directing complaint calls to that number.
	</p>
	<Image
		src={filingNoiseComplaintsCityBinder}
		alt="black and yellow three ring binder full of complaints"
	/>
	<p>
		Research local noise ordinance rules. You'll sound like you know what you're
		talking about and you can cite specific times your neighbors have made noise
		outside those limits.
	</p>
	<p>
		If you have a landlord, make sure you've contacted them before you contact
		the police or anyone else. You need to at least give them a chance to
		resolve the situation.
	</p>
</Section>

<Section id="ordinance">
	<h2>What Are the Parameters of the Noise Ordinance</h2>
	<p>
		Noise ordinances restrict noise duration, level of noise, source of noise
		(proximity to dwelling), zoning of area, and time of day. Unfortunately,
		some of these things are subjective. Many of the ordinances themselves are
		filled with subjective language that is up to interpretation. How is one to
		enforce: “excessive noise”, “disturbing noise”, or “plainly audible”?
	</p>
	<Bubble>
		<h3 class="text-xl">Example: Portland, OR Noise Ordinances</h3>
		<p class="font-bold">
			No excessive noise is allowed in the City of Portland between the hours of
			10:00 PM and 7:00 am. To report noise during these hours, call
			503-823-3333 x2. Types of Noise Covered by the Code:
		</p>
		<ul class="pl-4">
			<li>Amplified soud</li>
			<li>Music</li>
			<li>Construction</li>
			<li>Events and parties</li>
			<li>Animal noises from legal animal facilities</li>
		</ul>

		<p class="font-bold">
			For questions, information, or concerns, please email the Noise Office or
			file using the online noise complaint form.
		</p>
	</Bubble>
	<p>
		Most people agree that higher levels of noise are acceptable during the
		daytime hours than at night. But, when do nighttime hours begin and what
		noises are acceptable? The use of power tools may have a curfew, but air
		conditioners can run all night long.
	</p>
	<p>
		If the ordinance has a fixed maximum sound level, it will include ambient
		noise in its measurement. The ambient noise may affect the reading of the
		sound level, but it means there only has to be one reading of the offending
		noise. Generally, the ambient noise is not an issue and the offending noise
		can be identified based on complaint descriptions.
	</p>
	<Image src={IwNoiseLevelInfographic} alt="ilustration of the noise levels" />
	<p>
		Continuous sound has more of an effect than short bursts of noise. Most
		ordinances cover “exceedance allowance”. Higher levels of sound are allowed
		for shorter amounts of time and lower levels of sound are allowed for longer
		amounts of time.
	</p>
	<p>
		Noise levels are measured by immission and emission. Immission levels are
		generally used for residential zones, measuring the amount of noise entering
		a space. Emission levels are usually used for noise sources, often
		commercial spaces and are designated to balance the health and welfare of
		residential dwellers and livelihood of business owners.
	</p>
	<p>
		To find your city's noise ordinances, you can search for “city state noise
		ordinance” or go to your city's government website. They should have a
		section on charter, code, and policies available for the public. Look in FAQ
		for City Ordinances if you can't find it elsewhere.
	</p>
</Section>

<Section id="file">
	<h2>How Can You File a Complaint, and Do it Anonymously</h2>
	<p>
		Measurements must be taken by someone who is not the listener. So, your
		recordings may be useful, but ultimately you need a third party. An official
		needs to access the “excessive noise” you're dealing with based on your
		city's noise ordinances.
	</p>
	<p>
		If you've found the noise ordinances on your local government website, keep
		searching for what types of noise they will and will not deal with. Some
		noise sources belong to other departments. For example, complaints about
		animal noises like barking dogs often go to animal control. Similarly,
		aircraft noise, train noise, and garbage noise complaints all go to
		different departments. It's best to call your local government to inquire
		where to call or file online.
	</p>
	<Image src={solutionsComfortSavingsPerformance} />
	<p>
		There should be a general non-emergency number to call that can direct you
		to file a noise complaint. If the noises you are hearing indicate there may
		be violence involved, call 911. You can also file a noise complaint online
		via the Noise Complaint Form, also found on your local government's website.
	</p>
	<p>
		Most forms ask that if you are reporting noise currently breaking
		ordinances, to call the non-emergency line immediately. If you are reporting
		an ongoing offense, use the form. They usually ask you to be specific. This
		is when all the terms above will help you describe the noise complaint in
		detail.
	</p>
	<p>
		Your information will be held confidential unless it is needed for testimony
		or you waive your right to confidentiality. Most forms include questions
		regarding your confidentiality, including your willingness to testify, so
		you can rest assured that you can make an anonymous noise complaint.
	</p>
</Section>

<Section id="noise">
	<h2>
		What Can You Do If the Noise Doesn't Stop? Or if They Are Within the
		Guidelines?
	</h2>
	<Image src={resourcesSoundproofing} class="max-w-44" />
	<p>
		It's possible that after all your research and filing a noise complaint
		online that you aren't able to make any progress. Or, the sound may fall
		within the limits of the law. Noise ordinances are complex and frustrating,
		but there are other solutions that you can take into your own hands.
	</p>
	<p>
		It may seem like you shouldn't have to take ownership of the issue, and you
		shouldn't give up the <a
			href="https://medium.com/@foskat22/noise-pollution-the-not-so-silent-killer-76caf02754aa"
			target="_blank"
			rel="noreferrer">fight against noise pollution</a
		>. But, soundproofing your home will save you pain, anger, and improve your
		health. Depending on the state of your home and budget, there are many
		avenues you can take to help you and your family.
	</p>
	<Bubble>
		<h3 class="mb-6">Soundproof your home by:</h3>
		<ul>
			<li>adding insulation to your walls and attic.</li>
			<li>placing rugs and other noise-absorbing material around your home.</li>
			<li>
				adding acoustic panels to your walls - this is especially helpful if one
				section of your home is affected by noise.
			</li>
			<li>
				get <a href="/solutions/window-noise-reduction"
					>soundproofing window inserts</a
				> that can block up to 75% of outside noise.
			</li>
		</ul>
	</Bubble>
	<Youtube
		posterImageSrc={thumb_u2ILNHzT8FM}
		src="https://www.youtube.com/embed/u2ILNHzT8FM"
		title="Soundproofing Windows with Indow Inserts"
	/>
</Section>

<Section>
	Living with Noise: Complaints, Ordinances, &amp; Solutions
	<p>
		The more you educate yourself about the ordinances of your city, the more
		empowered you are to fight for your silence. We all have to live with some
		noise, but sounds are rising around us every day and the more we document –
		through noise complaints or otherwise – the more we fight for our welfare.
		Many noise ordinances have not been updated for over 30 years and are behind
		the technology of noise-making products. Yes, soundproof your home, but also
		protect your right to domestic tranquility.
	</p>
</Section>
<GetAFreeEstimate />
